import React, { useEffect, useState, useRef } from "react";
import {
  Box, Grid, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Typography, Menu, MenuItem,
  IconButton, Button, CircularProgress, Tooltip, Divider, TextField,
  useMediaQuery,
  useTheme,
  Collapse,
} from "@mui/material";
import {
  ArrowDropDown as ArrowDropDownIcon,
  Description as FileIcon, ExpandMore,
  ExpandLess,
} from "@mui/icons-material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { StockLedgerList, StockLedgerExport } from "../../../Services/Services";

const formatCurrency = (num) =>
  new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2 }).format(num || 0);

const StockLedgerTable = () => {
  const [ledgerData, setLedgerData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalIn, setTotalIn] = useState(0);
  const [totalOut, setTotalOut] = useState(0);
  const [currentBalance, setCurrentBalance] = useState(0);
  const [excelLoading, setExcelLoading] = useState(false);

  // ✅ Date-range state
  const [dateRange, setDateRange] = useState("today");
  const [customDates, setCustomDates] = useState({ from: "", to: "" });
  const [menuEl, setMenuEl] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [expandedLedger, setExpandedLedger] = useState(null);
  const [showCustomDateInputs, setShowCustomDateInputs] = useState(false);
  const [tempCustomDates, setTempCustomDates] = useState({ from: "", to: "" });


  // ============== Date Helpers ==============
  const formatDisplayDate = (d) => {
    if (!d) return "";
    const [y, m, day] = d.split("-");
    return `${day}-${m}-${y}`;
  };

  const getDateRangeDisplay = (rangeType) => {
    const now = new Date();
    const formatDate = (date) => {
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    };

    switch (rangeType) {
      case "today":
        return formatDate(now);
      case "yesterday":
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        return formatDate(yesterday);
      case "this_week":
        const weekStart = new Date(now);
        weekStart.setDate(weekStart.getDate() - weekStart.getDay() + 1);
        return `${formatDate(weekStart)} to ${formatDate(now)}`;
      case "previous_week":
        const prevWeekEnd = new Date(now);
        prevWeekEnd.setDate(prevWeekEnd.getDate() - prevWeekEnd.getDay());
        const prevWeekStart = new Date(prevWeekEnd);
        prevWeekStart.setDate(prevWeekStart.getDate() - 6);
        return `${formatDate(prevWeekStart)} to ${formatDate(prevWeekEnd)}`;
      case "this_month":
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        return `${formatDate(monthStart)} to ${formatDate(now)}`;
      case "current_year":
        const yearStart = new Date(now.getFullYear(), 0, 1);
        return `${formatDate(yearStart)} to ${formatDate(now)}`;
      case "financial_year":
        let fyStart;
        if (now.getMonth() >= 3) {
          fyStart = new Date(now.getFullYear(), 3, 1);
        } else {
          fyStart = new Date(now.getFullYear() - 1, 3, 1);
        }
        return `${formatDate(fyStart)} to ${formatDate(now)}`;
      default:
        return "";
    }
  };

  // ============== Fetch Stock Ledger ==============
  const fetchStockLedger = async (filters = {}) => {
    setLoading(true);
    try {
      const res = await StockLedgerList(filters);
      const rows = res?.results?.results || [];
      setLedgerData(Array.isArray(rows) ? rows : []);
      setTotalIn(res.total_in || 0);
      setTotalOut(res.total_out || 0);
      setCurrentBalance(res.current_balance || 0);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch stock ledger");
    } finally {
      setLoading(false);
    }
  };

  // Auto-fetch on filter change
  useEffect(() => {
    const handler = setTimeout(() => {
      const params = {};
      params.date_range = dateRange;
      if (dateRange === "custom" && customDates.from && customDates.to) {
        params.from_date = customDates.from;
        params.to_date = customDates.to;
      }

      fetchStockLedger(params);
    }, 500);

    return () => clearTimeout(handler);
  }, [dateRange, customDates]);

  // ============== Excel Export ==============
  const handleExport = async () => {
    setExcelLoading(true);
    try {
      const params = {};
      params.date_range = dateRange;
      if (dateRange === "custom" && customDates.from && customDates.to) {

        params.from_date = customDates.from;
        params.to_date = customDates.to;
      }

      const blob = await StockLedgerExport(params);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Stock_Ledger_${Date.now()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success("Excel downloaded successfully!");
    } catch (err) {
      console.error(err);
      toast.error("Export failed");
    } finally {
      setExcelLoading(false);
    }
  };

  const handleReset = () => {
    setDateRange("this_month");
    setCustomDates({ from: "", to: "" });
    // setSelectingDate(null);
    setTempCustomDates({ from: "", to: "" }); // ✅ Add
    setShowCustomDateInputs(false); // ✅ Add
  };

  // ✅ Handle Custom Date Range Apply
  const handleApplyCustomDateRange = () => {
    if (tempCustomDates.from && tempCustomDates.to) {
      setCustomDates({
        from: tempCustomDates.from,
        to: tempCustomDates.to,
      });
      setDateRange("custom");
      setMenuEl(null);
      setShowCustomDateInputs(false);
    } else {
      toast.warning("Please select both From and To dates");
    }
  };

  // ✅ Handle Cancel Custom Date Range
  const handleCancelCustomDateRange = () => {
    setTempCustomDates({ from: "", to: "" });
    setShowCustomDateInputs(false);
  };

  return (
    <Box p={2} sx={{ mt: -3, pt: 0, mb: 2 }}>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* ✅ Date Filter - Same as original */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={10}>
          <TextField
            label="Date Range"
            value={
              dateRange === "custom" && customDates.from && customDates.to
                ? `${formatDisplayDate(customDates.from)} to ${formatDisplayDate(customDates.to)}`
                : dateRange === "today"
                  ? "Today"
                  : dateRange === "yesterday"
                    ? "Yesterday"
                    : dateRange === "this_week"
                      ? "This Week"
                      : dateRange === "previous_week"
                        ? "Previous Week"
                        : dateRange === "this_month"
                          ? "This Month"
                          : dateRange === "current_year"
                            ? "Current Year"
                            : dateRange === "financial_year"
                              ? "Financial Year"
                              : "Select Date Range"
            }
            onClick={(e) => setMenuEl(e.currentTarget)}
            size="small"
            InputProps={{
              readOnly: true,
              endAdornment: (
                <IconButton size="small" edge="end">
                  <ArrowDropDownIcon />
                </IconButton>
              ),
            }}
            sx={{
              cursor: "pointer",
              "& .MuiInputBase-root": {
                height: 35,
                borderRadius: 0,
              },
              "& .MuiInputLabel-root": {
                fontSize: "0.7rem",
              },
            }}
            fullWidth
          />
        </Grid>

        <Grid item xs={12} sm={1.5}>
          <Button
            variant="outlined"
            onClick={handleReset}
            sx={{
              height: "35px",
              whiteSpace: "nowrap",
              borderRadius: "0",
              fontSize: "0.7rem",
            }}
            fullWidth
          >
            Reset
          </Button>
        </Grid>

        <Grid item xs={12} sm={0.5}>
          <Tooltip title={excelLoading ? "Exporting..." : "Download Excel"} arrow>
            <span>
              <IconButton
                color="success"
                size="small"
                onClick={handleExport}
                disabled={excelLoading}
              >
                {excelLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <FileIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Grid>
      </Grid>

      {/* Date Range Menu - UPDATED */}
      <Menu
        anchorEl={menuEl}
        open={Boolean(menuEl)}
        onClose={() => {
          setMenuEl(null);
          setShowCustomDateInputs(false);
          setTempCustomDates({ from: "", to: "" });
        }}
        PaperProps={{ sx: { width: 350, maxHeight: 500 } }}
      >
        {[
          { key: "today", label: "Today" },
          { key: "yesterday", label: "Yesterday" },
          { key: "this_week", label: "This Week" },
          { key: "previous_week", label: "Previous Week" },
          { key: "this_month", label: "This Month" },
          { key: "current_year", label: "Current Year" },
          { key: "financial_year", label: "Financial Year" },
        ].map((range) => (
          <MenuItem
            key={range.key}
            onClick={() => {
              setDateRange(range.key);
              setCustomDates({ from: "", to: "" });
              setMenuEl(null);
              setShowCustomDateInputs(false);
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <Typography variant="body2">{range.label}</Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "0.65rem" }}
              >
                {getDateRangeDisplay(range.key)}
              </Typography>
            </Box>
          </MenuItem>
        ))}

        <Divider />

        {/* ✅ Custom Date Range - Expandable */}
        <MenuItem
          onClick={() => setShowCustomDateInputs(!showCustomDateInputs)}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: showCustomDateInputs ? "#e3f2fd" : "inherit",
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: showCustomDateInputs ? 600 : 400 }}>
            Custom Date Range
          </Typography>
          {showCustomDateInputs ? <ExpandLess /> : <ExpandMore />}
        </MenuItem>

        {/* ✅ Both Date Pickers Together */}
        <Collapse in={showCustomDateInputs} timeout="auto" unmountOnExit>
          <Box
            sx={{
              px: 2,
              py: 2,
              bgcolor: "#f5f5f5",
              borderTop: "1px solid #e0e0e0",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
              Select Date Range
            </Typography>

            {/* From and To Date Inputs Side by Side */}
            <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
              <TextField
                label="From Date"
                type="date"
                size="small"
                value={tempCustomDates.from}
                onChange={(e) =>
                  setTempCustomDates((prev) => ({
                    ...prev,
                    from: e.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  max: tempCustomDates.to || new Date().toISOString().split("T")[0],
                }}
                sx={{
                  flex: 1,
                  "& .MuiInputBase-root": {
                    height: 40,
                    fontSize: "0.75rem",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.75rem",
                  },
                }}
              />

              <TextField
                label="To Date"
                type="date"
                size="small"
                value={tempCustomDates.to}
                onChange={(e) =>
                  setTempCustomDates((prev) => ({
                    ...prev,
                    to: e.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: tempCustomDates.from || undefined,
                  max: new Date().toISOString().split("T")[0],
                }}
                sx={{
                  flex: 1,
                  "& .MuiInputBase-root": {
                    height: 40,
                    fontSize: "0.75rem",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.75rem",
                  },
                }}
              />
            </Box>

            {/* Selected Range Preview */}
            {tempCustomDates.from && tempCustomDates.to && (
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mb: 1.5,
                  p: 1,
                  bgcolor: "#e8f5e9",
                  borderRadius: 1,
                  textAlign: "center",
                  color: "#2e7d32",
                  fontWeight: 500,
                }}
              >
                📅 {formatDisplayDate(tempCustomDates.from)} to{" "}
                {formatDisplayDate(tempCustomDates.to)}
              </Typography>
            )}

            {/* Apply and Cancel Buttons */}
            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleCancelCustomDateRange}
                sx={{
                  fontSize: "0.7rem",
                  textTransform: "none",
                  borderRadius: 1,
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={handleApplyCustomDateRange}
                disabled={!tempCustomDates.from || !tempCustomDates.to}
                sx={{
                  fontSize: "0.7rem",
                  textTransform: "none",
                  borderRadius: 1,
                }}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </Collapse>
      </Menu>

      {/* ✅ Mobile/Desktop Conditional Rendering */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress size={40} />
        </Box>
      ) : ledgerData.length === 0 ? (
        <Box sx={{ textAlign: "center", p: 4 }}>
          <Typography color="text.secondary">No entries found</Typography>
        </Box>
      ) : isMobile ? (
        /* 📱 MOBILE VIEW - Collapsible Cards */
        <Box sx={{ bgcolor: "background.paper" }}>
          {ledgerData.map((row, index) => (
            <Box key={row.id || index}>
              <Box
                onClick={() =>
                  setExpandedLedger(expandedLedger === row.id ? null : row.id)
                }
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 1.5,
                  cursor: "pointer",
                  bgcolor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                  "&:active": { bgcolor: "#e3f2fd" },
                  borderLeft: "4px solid #2196f3",
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 0.5,
                    }}
                  >
                    <Typography
                      sx={{ fontSize: "0.7rem", color: "#666", minWidth: 25 }}
                    >
                      #{index + 1}
                    </Typography>
                    <Typography
                      sx={{ fontWeight: "bold", fontSize: "0.85rem", flex: 1 }}
                    >
                      {row.item_name || "N/A"}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      color: "#666",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <span style={{ fontWeight: 500 }}>
                      {row.transaction_type || "-"}
                    </span>
                    <span style={{ color: "#999" }}>•</span>
                    <span style={{ fontSize: "0.7rem" }}>{row.date}</span>
                  </Typography>
                </Box>
                <IconButton size="small" sx={{ ml: 1 }}>
                  {expandedLedger === row.id ? (
                    <ExpandLess fontSize="small" />
                  ) : (
                    <ExpandMore fontSize="small" />
                  )}
                </IconButton>
              </Box>

              <Collapse
                in={expandedLedger === row.id}
                timeout="auto"
                unmountOnExit
              >
                <Box
                  sx={{
                    px: 1.5,
                    py: 1,
                    bgcolor: index % 2 === 0 ? "#f8f9fa" : "#fafafa",
                    borderLeft: "4px solid #2196f3",
                  }}
                >
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 0.8,
                      mb: 1,
                    }}
                  >
                    <Box sx={{ gridColumn: "1 / -1" }}>
                      <Typography
                        sx={{
                          fontSize: "0.6rem",
                          color: "#999",
                          letterSpacing: "0.5px",
                        }}
                      >
                        ITEM NAME
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          fontWeight: 500,
                          color: "#333",
                        }}
                      >
                        {row.item_name || "-"}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography
                        sx={{
                          fontSize: "0.6rem",
                          color: "#999",
                          letterSpacing: "0.5px",
                        }}
                      >
                        DATE
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.72rem",
                          fontWeight: 500,
                          color: "#555",
                        }}
                      >
                        {row.date || "-"}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography
                        sx={{
                          fontSize: "0.6rem",
                          color: "#999",
                          letterSpacing: "0.5px",
                        }}
                      >
                        CATEGORY
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.72rem",
                          fontWeight: 500,
                          color: "#555",
                        }}
                      >
                        {row.category_name || "-"}
                      </Typography>
                    </Box>

                    {/* <Box>
                      <Typography
                        sx={{
                          fontSize: "0.6rem",
                          color: "#999",
                          letterSpacing: "0.5px",
                        }}
                      >
                        SUB CATEGORY
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.72rem",
                          fontWeight: 500,
                          color: "#555",
                        }}
                      >
                        {row.sub_category_name || "-"}
                      </Typography>
                    </Box> */}

                    <Box>
                      <Typography
                        sx={{
                          fontSize: "0.6rem",
                          color: "#999",
                          letterSpacing: "0.5px",
                        }}
                      >
                        ITEM TYPE
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.72rem",
                          fontWeight: 500,
                          color: "#555",
                        }}
                      >
                        {row.item_type || "-"}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography
                        sx={{
                          fontSize: "0.6rem",
                          color: "#999",
                          letterSpacing: "0.5px",
                        }}
                      >
                        GRN NO
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.72rem",
                          fontWeight: 500,
                          color: "#555",
                        }}
                      >
                        {row.grn_no || "-"}
                      </Typography>
                    </Box>

                    <Box sx={{ gridColumn: "1 / -1" }}>
                      <Typography
                        sx={{
                          fontSize: "0.6rem",
                          color: "#999",
                          letterSpacing: "0.5px",
                        }}
                      >
                        TRANSACTION TYPE
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.72rem",
                          fontWeight: 500,
                          color: "#555",
                        }}
                      >
                        {row.transaction_type || "-"}
                      </Typography>
                    </Box>

                    <Box sx={{ gridColumn: "1 / -1" }}>
                      <Typography
                        sx={{
                          fontSize: "0.6rem",
                          color: "#999",
                          letterSpacing: "0.5px",
                        }}
                      >
                        REFERENCE NO
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.72rem",
                          fontWeight: 500,
                          color: "#555",
                        }}
                      >
                        {row.reference_no || "-"}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography
                        sx={{
                          fontSize: "0.6rem",
                          color: "#999",
                          letterSpacing: "0.5px",
                        }}
                      >
                        QTY IN
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.72rem",
                          fontWeight: 600,
                          color: row.quantity_in > 0 ? "#4caf50" : "#999",
                        }}
                      >
                        {row.quantity_in || "-"}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography
                        sx={{
                          fontSize: "0.6rem",
                          color: "#999",
                          letterSpacing: "0.5px",
                        }}
                      >
                        QTY OUT
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.72rem",
                          fontWeight: 600,
                          color: row.quantity_out > 0 ? "#f44336" : "#999",
                        }}
                      >
                        {row.quantity_out || "-"}
                      </Typography>
                    </Box>

                    <Box sx={{ gridColumn: "1 / -1" }}>
                      <Typography
                        sx={{
                          fontSize: "0.6rem",
                          color: "#999",
                          letterSpacing: "0.5px",
                        }}
                      >
                        BALANCE QTY
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.8rem",
                          fontWeight: 700,
                          color: "#2196f3",
                        }}
                      >
                        {row.balance_qty || 0}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Collapse>
              <Divider />
            </Box>
          ))}
        </Box>
      ) : (
        /* 💻 DESKTOP VIEW - Original Table */
        <TableContainer
          component={Paper}
          sx={{
            maxHeight: "73vh",
            overflow: "auto",
            border: "1px solid #e0e0e0",
            borderRadius: 1,
          }}
        >
          <Table
            size="small"
            stickyHeader
            sx={{
              minWidth: 1000,
              "& td, & th": {
                textAlign: "center",
                whiteSpace: "nowrap",
                borderBottom: "1px solid #ccc",
              },
            }}
          >
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: "#f5f5f5",
                  "& th": {
                    fontWeight: "bold",
                    color: "#333",
                    borderBottom: "1px solid #999",
                    backgroundColor: "#f5f5f5",
                  },
                }}
              >
                <TableCell>S.No</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Category</TableCell>
                {/* <TableCell>Sub Category</TableCell> */}
                <TableCell>Item Type</TableCell>
                <TableCell>Item</TableCell>
                <TableCell>GRN No.</TableCell>
                <TableCell>Transaction Type</TableCell>
                <TableCell>Reference No</TableCell>
                <TableCell>Qty In</TableCell>
                <TableCell>Qty Out</TableCell>
                <TableCell>Balance</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {ledgerData.map((row, index) => (
                <TableRow
                  key={row.id || index}
                  sx={{
                    backgroundColor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                    "& td": {
                      paddingTop: "2px !important",
                      paddingBottom: "2px !important",
                      fontSize: { xs: "0.75rem", sm: "0.8125rem" },
                    },
                    "&:hover": {
                      backgroundColor: "#e3f2fd !important",
                    },
                  }}
                >
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{row.date}</TableCell>
                  <TableCell>{row.category_name}</TableCell>
                  {/* <TableCell>{row.sub_category_name}</TableCell> */}
                  <TableCell>{row.item_type}</TableCell>
                  <TableCell>{row.item_name}</TableCell>
                  <TableCell>{row.grn_no}</TableCell>
                  <TableCell>{row.transaction_type}</TableCell>
                  <TableCell>{row.reference_no}</TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      color: row.quantity_in > 0 ? "success.main" : "text.disabled",
                    }}
                  >
                    {row.quantity_in || "-"}
                  </TableCell>
                  <TableCell
                    sx={{
                      color: row.quantity_out > 0 ? "error.main" : "text.disabled",
                    }}
                  >
                    {row.quantity_out || "-"}
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    {row.balance_qty}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default StockLedgerTable;